import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { generateImage, enhancePrompt, ALL_PROVIDERS } from '../lib/imageApi';
import { bidturnsAnalyze, newSessionId } from '../lib/bidturns';
import PanditLogo from '../components/PanditLogo';
import ProductAd from '../components/ProductAd';
import QuotaModal from '../components/QuotaModal';
import ReportModal from '../components/ReportModal';
import AiContentNotice from '../components/AiContentNotice';
import { useUsage } from '../contexts/UsageContext';
import './ImagePage.css';

// Style tiles are real reference images now (not CSS gradients) — they
// were extracted from the final HTML and dropped into public/styles/.
// Each file is 5-18 KB; CRA serves them at /styles/<slug>.jpg.
const STYLE_TILE = (slug) => `${process.env.PUBLIC_URL || ''}/styles/${slug}.jpg`;

// Style is appended to the prompt; dimensions are visual only for now
// (backend doesn't accept aspect-ratio); count fans out N parallel calls
// to the chosen provider.
const MODEL_OPTIONS = ALL_PROVIDERS.map((p) => ({
  value: p.id,
  title: p.label,
  sub: p.model,
  color: ({
    hf: 'linear-gradient(135deg,#f97316,#f59e0b)',
    pollinations: 'linear-gradient(135deg,#10b981,#059669)',
    'stable-core': 'linear-gradient(135deg,#8b5cf6,#6d28d9)',
    'stable-ultra': 'linear-gradient(135deg,#a855f7,#7e22ce)',
    sd35: 'linear-gradient(135deg,#3b82f6,#1d4ed8)',
  }[p.id]) || 'linear-gradient(135deg,#7c5cfc,#e96c6c)',
}));

// 8 styles per the final design — each tile is a real reference image
// rather than a gradient. `tail` is appended to the user's prompt before
// the API call; `tileBg` drives the visual tile.
const STYLES = [
  { label: 'Cinematic', short: 'Cinematic', sub: 'Dramatic · Film-like',
    tail: ', cinematic lighting, dramatic shadows, film grain, ultra detailed',
    tileBg: `url("${STYLE_TILE('cinematic')}") center/cover` },
  { label: 'Headshot',  short: 'Headshot',  sub: 'Portrait · Professional',
    tail: ', professional headshot, studio lighting, sharp focus, portrait',
    tileBg: `url("${STYLE_TILE('headshot')}") center/cover` },
  { label: 'Ghibli',    short: 'Ghibli',    sub: 'Whimsical · Painted',
    tail: ', Studio Ghibli style, hand-painted, whimsical, soft palette',
    tileBg: `url("${STYLE_TILE('ghibli')}") center/cover` },
  { label: 'Bronze',    short: 'Bronze',    sub: 'Sculpted · Metallic',
    tail: ', bronze sculpture, polished metal, classical artistry',
    tileBg: `url("${STYLE_TILE('bronze')}") center/cover` },
  { label: '3D render', short: '3D',        sub: 'Volumetric · CGI',
    tail: ', 3D render, volumetric lighting, octane render',
    tileBg: `url("${STYLE_TILE('3d-render')}") center/cover` },
  { label: 'Oil paint', short: 'Oil',       sub: 'Rich · Textured',
    tail: ', oil painting, rich brushstrokes, classical art',
    tileBg: `url("${STYLE_TILE('oil-paint')}") center/cover` },
  { label: '90s Style', short: '90s',       sub: 'Retro · Analog',
    tail: ', 90s photography style, analog film, retro colours, slight grain',
    tileBg: `url("${STYLE_TILE('90s-style')}") center/cover` },
  { label: 'Anime',     short: 'Anime',     sub: 'Japanese · Stylised',
    tail: ', anime style, vibrant colours, cel shading',
    tileBg: `url("${STYLE_TILE('anime')}") center/cover` },
];

const DIMS = ['1:1', '2:3', '16:9', '3:4', '9:16'];

// Number of generations is fixed at 1 — the picker was removed per
// product direction. Keeping the constant as a sanity marker for the
// batch shape (each batch has exactly one card).
const GEN_COUNT = 1;

function ratioCss(r) {
  return ({ '1:1': '1 / 1', '2:3': '2 / 3', '16:9': '16 / 9', '3:4': '3 / 4', '9:16': '9 / 16' }[r] || '1 / 1');
}

// Prompt-only persistence: we save what the user TYPED, never the
// generated bytes. A 1024×1024 PNG is ~1.3 MB base64 — five batches
// at count=4 would blow the localStorage quota. The prompt text is
// what's actually useful for a "rerun this" history list anyway.
//
// New key (v2). Old v1 history (which contained image data-URLs) is
// ignored on hydrate; users start fresh with the cleaner schema.
const HISTORY_KEY = 'arthum.imgHistory.v2';
const MAX_BATCHES = 50;

function loadHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}
// Persist a LEAN copy — strip cards.imageDataUrl entirely. The lean
// batch is still renderable: cards show status='archived' instead of
// 'ok', which the render layer treats as "image not retained, click
// to regenerate".
function saveHistory(batches) {
  const persistable = batches
    .map((b) => ({
      ...b,
      cards: b.cards.map((c) => ({
        id: c.id,
        // Anything that's not still loading becomes 'archived' on
        // persist. Loading cards become 'error' so a refresh mid-gen
        // doesn't leave them stuck shimmering forever.
        status: c.status === 'loading' ? 'error' : 'archived',
        ...(c.error ? { error: c.error } : null),
        ...(c.latencyMs != null ? { latencyMs: c.latencyMs } : null),
      })),
    }))
    .slice(0, MAX_BATCHES);
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(persistable));
    // Tell the sidebar to refresh its mirror — same-tab setItem
    // doesn't trigger 'storage' events, so we ping a custom event.
    window.dispatchEvent(new Event('arthum:img-history-changed'));
  }
  catch { /* quota exceeded — silently drop the write */ }
}

// Convert a data-URL to a Blob/File and trigger a save. On Android/iOS
// WebView the <a download> pattern is broken for data URLs — the Web
// Share API (level 2, with files) works reliably instead. Falls back to
// a programmatic blob-URL click for desktop browsers.
function saveImage(dataUrl, filename) {
  try {
    const parts = dataUrl.split(',');
    const mime = (parts[0].match(/:(.*?);/) || [])[1] || 'image/png';
    const raw = atob(parts[1]);
    const u8 = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) u8[i] = raw.charCodeAt(i);
    const blob = new Blob([u8], { type: mime });
    const file = new File([blob], filename, { type: mime });
    if (navigator.canShare?.({ files: [file] })) {
      navigator.share({ files: [file] }).catch(() => {});
      return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 200);
  } catch { /* silent */ }
}

function ImagePage({ active = true, onSubscribe }) {
  const { bump: bumpUsage } = useUsage();
  const [model, setModel]     = useState(MODEL_OPTIONS[0].value);
  const [style, setStyle]     = useState(STYLES[0].label);
  const [dim, setDim]         = useState('1:1');
  const [prompt, setPrompt]   = useState('');
  const [busy, setBusy]       = useState(false);
  // Limit modal state — populated when generation 429s.
  const [quotaUsage, setQuotaUsage] = useState(null);
  const [quotaKind, setQuotaKind] = useState(null);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  // Content-report modal target: null when closed, else { content, meta }
  // describing the generated image being flagged.
  const [reportTarget, setReportTarget] = useState(null);
  // Each batch = one Generate click. New batches PREPEND so the most
  // recent work is at the top — same pattern as a chat history.
  //   { id, prompt, style, dim, model, createdAt, cards: [...] }
  const [batches, setBatches] = useState(loadHistory);
  // Ref to the scrollable area so we can snap to the top whenever a
  // new batch is prepended. Smooth scroll feels good; the user sees
  // the new batch sliding in from above.
  const scrollRef = useRef(null);
  // One BidTurns session for the whole image surface — ties multiple
  // prompts the same user runs into a single intent context. Stable
  // for the life of the page mount; refreshed on full page reload.
  const sessionIdRef = useRef(newSessionId());

  // Persist whenever batches change. Debounced indirectly via React's
  // batching; setBatches inside an async loop triggers many renders
  // but localStorage.setItem on each is cheap (~ms).
  useEffect(() => { saveHistory(batches); }, [batches]);

  const [filterSlot, setFilterSlot] = useState(null);
  useEffect(() => {
    const el = document.getElementById('sb-img-filters-slot');
    setFilterSlot(el);
  }, []);

  const handleGen = async (promptOverride) => {
    // rerunBatch calls this immediately after setPrompt(), which means
    // the closure's `prompt` is still stale on that frame. Allow the
    // caller to pass the new prompt directly to dodge the stale read.
    const q = (typeof promptOverride === 'string' ? promptOverride : prompt).trim();
    if (!q || busy) return;
    setBusy(true);
    const tail = STYLES.find((s) => s.label === style)?.tail || '';
    const enriched = (q + tail).slice(0, 1800);

    // Best-effort enhancer — Claude/GPT prompt rewriter on the server.
    let finalPrompt = enriched;
    try {
      const r = await enhancePrompt({ prompt: enriched, enhancer: 'none' });
      if (r?.enhanced) finalPrompt = r.enhanced;
    } catch { /* ignore */ }

    const batchId = `b-${Date.now()}`;
    const newBatch = {
      id: batchId,
      prompt: q,
      style,
      dim,
      model,
      createdAt: Date.now(),
      cards: Array.from({ length: GEN_COUNT }, (_, i) => ({
        id: `${batchId}-c${i}`,
        status: 'loading',
        startedAt: Date.now(),
      })),
    };
    // PREPEND — newest at top. Older batches are still scroll-reachable.
    setBatches((prev) => [newBatch, ...prev]);
    setPrompt('');

    // Best-effort BidTurns lookup against the user's RAW prompt (style/
    // model qualifiers would just dilute purchase intent). Never throws.
    // Result lands on the batch when it resolves — image generation
    // proceeds in parallel and isn't blocked on the ad call.
    bidturnsAnalyze({ query: q, sessionId: sessionIdRef.current })
      .then((payload) => {
        if (!payload?.adData?.product_name) return;
        setBatches((prev) => prev.map((b) =>
          b.id !== batchId ? b : { ...b, ad: payload.adData, intent: payload.intent || null }
        ));
      })
      .catch(() => { /* silent */ });
    // Snap to the top so the new shimmer is in view. Smooth scroll +
    // requestAnimationFrame so it happens AFTER React commits the new
    // batch DOM. The slide-in animation on .gen-batch handles the
    // visual reinforcement.
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' });
    });

    // Helper: patch one card inside the right batch.
    const patchCard = (cardId, patch) => {
      setBatches((prev) => prev.map((b) =>
        b.id !== batchId ? b : {
          ...b,
          cards: b.cards.map((c) => c.id === cardId ? { ...c, ...patch } : c),
        }
      ));
    };

    await Promise.allSettled(
      newBatch.cards.map(async (c) => {
        try {
          const out = await generateImage({ provider: model, prompt: finalPrompt, dim });
          if (out?.usage) bumpUsage(out.usage);
          patchCard(c.id, { status: 'ok', imageDataUrl: out.imageDataUrl, latencyMs: out.latencyMs });
        } catch (err) {
          // 429 = image quota exhausted. Stamp the card as error and
          // pop the upgrade modal so the user has somewhere to go.
          if (err?.status === 429 || err?.code === 'QUOTA_EXCEEDED') {
            patchCard(c.id, { status: 'error', error: 'Image limit reached' });
            if (err.usage) { setQuotaUsage(err.usage); bumpUsage(err.usage); }
            if (err.kind) setQuotaKind(err.kind);
            setShowQuotaModal(true);
            return;
          }
          patchCard(c.id, { status: 'error', error: err?.message || 'failed' });
        }
      })
    );
    setBusy(false);
  };

  const clearHistory = () => {
    if (!batches.length) return;
    if (!window.confirm('Clear all generated image history?')) return;
    setBatches([]);
  };

  // Rerun a past batch: copy its settings into the form, then trigger
  // a fresh generation. The new batch prepends just like any other.
  const rerunBatch = (b) => {
    if (busy) return;
    setPrompt(b.prompt);
    setStyle(b.style);
    setDim(b.dim);
    setModel(b.model);
    // Defer handleGen by a tick so the state writes above settle before
    // we read them inside it. We pass the prompt explicitly to bypass
    // the closure-stale-state problem.
    setTimeout(() => handleGen(b.prompt), 0);
  };

  // Per-card retry with a SPECIFIC provider. Unlike rerunBatch (which
  // creates a fresh batch), this patches the existing card back to
  // loading and runs the chosen model in-place. Used by the error-state
  // "try another model" chip row.
  const retryCardWithModel = async (batch, card, nextModelId) => {
    // Reuse the existing prompt-enhancement output if we still have it
    // — we don't, so just re-enhance for safety. This is the rare path,
    // a few hundred extra ms is fine.
    const tail = STYLES.find((s) => s.label === batch.style)?.tail || '';
    const enriched = (batch.prompt + tail).slice(0, 1800);
    let finalPrompt = enriched;
    try {
      const r = await enhancePrompt({ prompt: enriched, enhancer: 'none' });
      if (r?.enhanced) finalPrompt = r.enhanced;
    } catch { /* ignore */ }

    // Flip the card back to loading. Remember the provider on the
    // card so the UI can label "Last try: stable-core failed".
    setBatches((prev) => prev.map((b) =>
      b.id !== batch.id ? b : {
        ...b,
        cards: b.cards.map((c) => c.id === card.id
          ? { ...c, status: 'loading', error: undefined, retryModel: nextModelId, startedAt: Date.now() }
          : c),
      }
    ));

    try {
      const out = await generateImage({ provider: nextModelId, prompt: finalPrompt, dim: batch.dim });
      if (out?.usage) bumpUsage(out.usage);
      setBatches((prev) => prev.map((b) =>
        b.id !== batch.id ? b : {
          ...b,
          cards: b.cards.map((c) => c.id === card.id
            ? { ...c, status: 'ok', imageDataUrl: out.imageDataUrl, latencyMs: out.latencyMs }
            : c),
        }
      ));
    } catch (err) {
      if (err?.status === 429 || err?.code === 'QUOTA_EXCEEDED') {
        if (err.usage) { setQuotaUsage(err.usage); bumpUsage(err.usage); }
        if (err.kind) setQuotaKind(err.kind);
        setShowQuotaModal(true);
      }
      setBatches((prev) => prev.map((b) =>
        b.id !== batch.id ? b : {
          ...b,
          cards: b.cards.map((c) => c.id === card.id
            ? { ...c, status: 'error', error: err?.message || 'failed', retryModel: nextModelId }
            : c),
        }
      ));
    }
  };

  // Sidebar clicks come in as custom events (Sidebar lives outside this
  // component's tree, no shared context). Listen for them and rerun.
  useEffect(() => {
    const onRerunEvent = (e) => {
      const batch = e.detail;
      if (batch?.prompt) rerunBatch(batch);
    };
    window.addEventListener('arthum:img-rerun', onRerunEvent);
    return () => window.removeEventListener('arthum:img-rerun', onRerunEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy]);   // rerunBatch closure captures `busy`; refresh when it flips

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleGen();
    }
  };

  const filters = (
    <FilterPanel
      model={model} setModel={setModel}
      style={style} setStyle={setStyle}
      dim={dim} setDim={setDim}
    />
  );

  // Show only batches generated DURING this page-mount as the main
  // view — ChatGPT-style "current session" flow. Persisted history
  // (from before this mount) is still in `batches` so the sidebar's
  // Image history reads it via localStorage, but we don't dump every
  // past prompt onto the canvas. When the session is empty, the
  // Pandit Ji welcome shows instead.
  const sessionStartRef = useRef(Date.now());
  const sessionBatches = batches.filter((b) => (b.createdAt || 0) >= sessionStartRef.current);
  const hasSession = sessionBatches.length > 0;

  // The prompt + Generate button + style tiles are the same UI in both
  // the welcome state and the post-generation footer — extracted to a
  // single subcomponent so we don't duplicate JSX (and don't desync the
  // two when a styling change ships).
  const promptBlock = (
    <>
      <div className="gen-box">
        <textarea
          className="gen-ta"
          placeholder="Describe the image…"
          rows={1}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={onKey}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <button className="gen-btn" onClick={handleGen} disabled={busy || !prompt.trim()}>
            {busy ? 'Generating…' : 'Generate'}
          </button>
        </div>
      </div>

      <div className="gen-style-tiles">
        {STYLES.map((s) => (
          <div
            key={s.label}
            className={`style-tile ${style === s.label ? 'on' : ''}`}
            onClick={() => setStyle(s.label)}
            title={s.label}
          >
            <div className="style-tile-bg" style={{ background: s.tileBg }} />
            <div className="style-tile-label">{s.short || s.label}</div>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div className={`page ${active ? 'on' : ''}`} id="pg-img">
      {filterSlot && ReactDOM.createPortal(filters, filterSlot)}
      {/* Unmissable top progress bar while any generation is in flight. */}
      {busy && <div className="img-page-progress" aria-hidden />}
      {showQuotaModal && (
        <QuotaModal
          kind={quotaKind}
          usage={quotaUsage}
          onClose={() => setShowQuotaModal(false)}
          onSubscribe={() => { setShowQuotaModal(false); onSubscribe?.(); }}
        />
      )}
      {lightboxSrc && (
        <ImageLightbox
          src={lightboxSrc}
          onClose={() => setLightboxSrc(null)}
          onReport={() => setReportTarget({ content: '', meta: { source: 'lightbox' } })}
        />
      )}
      {/* Report / flag offensive generated image (Google Play AI content
          policy). Opened from a card's Report button or the lightbox. */}
      <ReportModal
        open={!!reportTarget}
        onClose={() => setReportTarget(null)}
        kind="image"
        content={reportTarget?.content || ''}
        meta={reportTarget?.meta || {}}
      />

      <div className="img-shell">
        <div className="img-center">
          {!hasSession ? (
            // ── Welcome state ────────────────────────────────
            // Pandit Ji centred, subtitle, then the prompt + style
            // tiles. Shown whenever the current session is empty.
            <div className="img-welcome">
              <div className="welcome-logo-wrap" style={{ marginBottom: 12 }}>
                <PanditLogo size="lg" className="welcome-logo pandit-logo-welcome" alt="Sutrum AI" />
              </div>
              {/* Two-line welcome — same gradient + size as the Chat
                  page's "What can I help with?" so the three surfaces
                  (Chat / Image / Characters) read as one family. */}
              <div className="img-welcome-h">Create Images</div>
              <div className="img-welcome-sub">
                Select style and describe your Image
              </div>
              <div className="img-welcome-form">
                {promptBlock}
                {/* Also visible in the welcome state so the affordance
                    is findable BEFORE the user generates anything. This
                    is the exact screen Google Play flagged. */}
                <AiContentNotice kind="image" meta={{ page: 'image-generator-welcome' }} />
              </div>
            </div>
          ) : (
            // ── Current-session state (ChatGPT-style flow) ───
            // Only the batches generated this mount are rendered.
            // No "Image history" header — that's a sidebar concern.
            // Prompt bar sticks at the bottom for iteration.
            <>
              <div className="gen-scroll" ref={scrollRef}>
                {sessionBatches.map((b) => (
                  <React.Fragment key={b.id}>
                    <ImageBatch
                      batch={b}
                      onRerun={rerunBatch}
                      onRetryCardWithModel={retryCardWithModel}
                      onViewImage={setLightboxSrc}
                      onReport={(batch) => setReportTarget({
                        content: batch.prompt,
                        meta: { model: batch.model, style: batch.style, dim: batch.dim, batchId: batch.id },
                      })}
                    />
                    {/* Inline sponsored card — appears between batches
                        ONLY when BidTurns matched a real product against
                        this batch's prompt. ProductAd self-gates on
                        ad.product_name so this won't render noise. */}
                    {b.ad && (
                      <div className="gen-batch-ad">
                        <ProductAd ad={b.ad} intent={b.intent} />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>

              <div className="gen-foot">
                <div className="gen-foot-inner">
                  {promptBlock}
                  {/* Persistent AI-content notice + Report affordance —
                      required by Play Store & App Store AI-content
                      policies. Sits inside the sticky prompt footer so
                      it's visible in every state (welcome + session). */}
                  <AiContentNotice kind="image" meta={{ page: 'image-generator' }} />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// One generation batch — header + grid. Pulled out so the JSX up there
// stays readable and so React can skip re-rendering completed batches
// when a different batch updates (the `b` prop is stable for done ones).
function ImageBatch({ batch, onRerun, onRetryCardWithModel, onViewImage, onReport }) {
  const { prompt: bPrompt, style: bStyle, dim: bDim, model: bModel, cards, createdAt } = batch;
  // Friendly label lookup for the model used in the failed attempt.
  const labelFor = (id) =>
    MODEL_OPTIONS.find((m) => m.value === id)?.title || id;
  const cols = cards.length === 1 ? '1fr' : 'repeat(2, 1fr)';
  const timeStr = (() => {
    try {
      return new Date(createdAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    } catch { return ''; }
  })();
  return (
    <div className="gen-batch">
      <div className="gen-batch-head">
        <div className="gen-batch-prompt" title={bPrompt}>{bPrompt}</div>
        <div className="gen-batch-meta">
          <span className="gen-batch-chip">{bStyle}</span>
          <span className="gen-batch-chip">{bDim}</span>
          <span className="gen-batch-chip gen-batch-chip-faint">{bModel}</span>
          {timeStr && <span className="gen-batch-time">{timeStr}</span>}
          {/* Re-run this exact prompt+settings combo. Useful for the
              archived state where the images aren't kept on disk. */}
          <button className="gen-batch-rerun" onClick={() => onRerun?.(batch)} title="Run this prompt again">
            ↻ Rerun
          </button>
        </div>
      </div>
      <div className="gen-grid" style={{ gridTemplateColumns: cols }}>
        {cards.map((c) => (
          <div
            key={c.id}
            className="img-card"
            style={{
              aspectRatio: ratioCss(bDim),
              ...(cards.length === 1 ? { maxWidth: 440, margin: '0 auto' } : null),
            }}
          >
            {c.status === 'loading' && (
              <div className="img-card-loading">
                {/* Layer 1: rotating conic rainbow — fake "image being
                    painted" backdrop. Blurred so it reads as paint,
                    not pixels. */}
                <div className="img-card-loading-bg" aria-hidden />
                {/* Layer 2: drifting bright blobs overlaid — adds the
                    "composition emerging" feel. */}
                <div className="img-card-loading-blobs" aria-hidden />
                {/* Layer 3: faint pixel grid — digital sampling feel. */}
                <div className="img-card-loading-grid" aria-hidden />
                {/* Layer 4: the scan line, sweeps top → bottom on loop. */}
                <div className="img-card-loading-scan" aria-hidden />
                {/* Floating status badge — corner, doesn't block the
                    fake background or scan reveal. */}
                <div className="img-card-loading-badge">
                  <span className="img-card-loading-badge-dot" aria-hidden />
                  <span>Generating · <ElapsedSeconds startedAt={c.startedAt} /></span>
                </div>
                <div className="img-card-loading-meta">
                  {bDim} · {bStyle} · {bModel}
                </div>
              </div>
            )}
            {c.status === 'ok' && (
              <>
                <img
                  src={c.imageDataUrl} alt="" className="img-card-fade-in"
                  onClick={() => onViewImage?.(c.imageDataUrl)}
                  style={{ cursor: 'pointer' }}
                />
                <div className="img-card-btns">
                  <button
                    className="ia-btn"
                    onClick={() => saveImage(c.imageDataUrl, `sutrum-${c.id}.png`)}
                  >Download</button>
                  {/* Flag offensive AI-generated image (Play policy). */}
                  <button
                    className="ia-btn ia-btn-report"
                    onClick={() => onReport?.(batch)}
                    title="Report this image"
                    aria-label="Report this image"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
                    Report
                  </button>
                  {c.latencyMs != null && (
                    <span className="ia-latency">{(c.latencyMs / 1000).toFixed(1)}s</span>
                  )}
                </div>
              </>
            )}
            {c.status === 'error' && (
              <div className="img-card-err">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <div className="img-card-err-headline">Generation failed</div>
                <div className="img-card-err-detail">
                  {labelFor(c.retryModel || bModel)} couldn't produce the image.
                  <br />
                  <span className="img-card-err-msg">{c.error}</span>
                </div>
                <div className="img-card-err-retry">
                  <span className="img-card-err-retry-label">Try another model:</span>
                  <div className="img-card-err-retry-row">
                    {MODEL_OPTIONS
                      .filter((m) => m.value !== (c.retryModel || bModel))
                      .map((m) => (
                        <button
                          key={m.value}
                          className="img-card-err-retry-chip"
                          onClick={() => onRetryCardWithModel?.(batch, c, m.value)}
                          title={`Retry with ${m.title}`}
                        >
                          {m.title}
                        </button>
                      ))}
                  </div>
                </div>
              </div>
            )}
            {c.status === 'archived' && (
              <button className="img-card-archived" onClick={() => onRerun?.(batch)}>
                <span className="img-card-archived-icon" aria-hidden>↻</span>
                <span className="img-card-archived-label">
                  Image not kept<br/>
                  <small>Click to regenerate</small>
                </span>
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Ticking seconds counter shown next to "Generating image…". Reinforces
// that the request is still in flight even when the network is slow.
function ElapsedSeconds({ startedAt }) {
  const [n, setN] = useState(() =>
    startedAt ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000)) : 0
  );
  useEffect(() => {
    if (!startedAt) return undefined;
    const id = setInterval(() => {
      setN(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  return <span>{n}s</span>;
}

function FilterPanel({ model, setModel, style, setStyle, dim, setDim }) {
  const [openDrop, setOpenDrop] = useState(null);
  const currentModel = MODEL_OPTIONS.find((m) => m.value === model) || MODEL_OPTIONS[0];
  const currentStyle = STYLES.find((s) => s.label === style) || STYLES[0];

  return (
    <>
      <div className="ig-section">
        <div className="ig-label">Model</div>
        <div className="ig-select-card" onClick={() => setOpenDrop(openDrop === 'model' ? null : 'model')}>
          <div className="ig-select-icon" style={{ background: currentModel.color }} />
          <div className="ig-select-text">
            <div className="ig-select-title">{currentModel.title}</div>
            <div className="ig-select-sub">{currentModel.sub}</div>
          </div>
          <svg className="ig-chevron" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
        </div>
        {openDrop === 'model' && (
          <div className="ig-dropdown open">
            {MODEL_OPTIONS.map((m) => (
              <div
                key={m.value}
                className={`ig-drop-opt ${model === m.value ? 'on' : ''}`}
                onClick={() => { setModel(m.value); setOpenDrop(null); }}
              >
                <div className="ig-drop-dot" style={{ background: m.color }} />
                {m.title}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="ig-section">
        <div className="ig-label">Style</div>
        <div className="ig-select-card" onClick={() => setOpenDrop(openDrop === 'style' ? null : 'style')}>
          <div className="ig-select-icon" style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}>
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth="2"><path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
          </div>
          <div className="ig-select-text">
            <div className="ig-select-title">{currentStyle.label}</div>
            <div className="ig-select-sub">{currentStyle.sub}</div>
          </div>
          <svg className="ig-chevron" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
        </div>
        {openDrop === 'style' && (
          <div className="ig-dropdown open">
            {STYLES.map((s) => (
              <div
                key={s.label}
                className={`ig-drop-opt ${style === s.label ? 'on' : ''}`}
                onClick={() => { setStyle(s.label); setOpenDrop(null); }}
              >
                {s.label}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="ig-section">
        <div className="ig-label">Image dimensions</div>
        <div className="ig-dim-grid">
          {DIMS.map((d) => {
            const [w, h] = d.split(':').map(Number);
            const max = 22;
            const dw = w >= h ? max : Math.round((w / h) * max);
            const dh = h >= w ? max : Math.round((h / w) * max);
            return (
              <div
                key={d}
                className={`ig-dim-btn ${dim === d ? 'on' : ''}`}
                onClick={() => setDim(d)}
              >
                <div className="ig-dim-icon">
                  <div style={{ width: dw, height: dh, border: '2px solid currentColor', borderRadius: 3 }} />
                </div>
                <span>{d}</span>
              </div>
            );
          })}
        </div>
      </div>

    </>
  );
}

function ImageLightbox({ src, onClose, onReport }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div className="img-lightbox" onClick={onClose}>
      <img src={src} alt="" onClick={(e) => e.stopPropagation()} />
      <button className="img-lightbox-close" onClick={onClose} aria-label="Close">×</button>
      <button
        className="img-lightbox-dl"
        onClick={(e) => { e.stopPropagation(); saveImage(src, `sutrum-${Date.now()}.png`); }}
      >
        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
        Save
      </button>
      {onReport && (
        <button
          className="img-lightbox-report"
          onClick={(e) => { e.stopPropagation(); onReport(); }}
        >
          <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
          Report
        </button>
      )}
    </div>,
    document.body
  );
}

export default ImagePage;
